import { useState } from 'react'
import { IRequestRecord } from 'types/requests.interface'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { formatDateForDisplay } from 'utils/helpers'
import Avatar from '../../../assets/images/profileAvatar.png'
import {
  typeLabel,
  statusLabel,
  statusPill,
  typePill,
  durationLabel,
  userName,
  REQ_SHIFT,
  REQ_OVERTIME,
  ST_PENDING,
  ST_APPROVED,
} from '../requestsData'
import {
  Scrim,
  Panel,
  Header,
  Identity,
  AvatarBox,
  IdentityText,
  Name,
  Meta,
  CloseBtn,
  Body,
  PillRow,
  PillGroup,
  Code,
  DateCards,
  DateCard,
  MiniLabel,
  MiniValue,
  MetaGrid,
  MetaItem,
  Reason,
  NoteLabel,
  NoteInput,
  DecisionBox,
  Actions,
} from './RequestDrawer.styled'

const submittedAgo = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'Today'
  return `${days}d ago`
}

export const RequestDrawer = ({
  req,
  scope,
  canApprove,
  currentUserId,
  onClose,
  onApprove,
  onReject,
  onCancel,
}: {
  req: IRequestRecord
  scope: 'approvals' | 'mine'
  canApprove: boolean
  currentUserId: number | null
  onClose: () => void
  onApprove: (note: string) => void
  onReject: (note: string) => void
  onCancel: () => void
}) => {
  const [note, setNote] = useState('')

  const pending = req.status === ST_PENDING
  const canDecide = canApprove && pending
  const canCancel =
    pending &&
    currentUserId !== null &&
    (req.requester?.id === currentUserId || req.createdBy?.id === currentUserId)

  return (
    <>
      <Scrim onClick={onClose} />
      <Panel>
        <Header>
          <Identity>
            <AvatarBox>
              <img src={Avatar} alt='' />
            </AvatarBox>
            <IdentityText>
              <Name>{userName(req.requester)}</Name>
              <Meta>
                {req.requester?.jobTitle || '—'}
                {req.requester?.department ? ` · ${req.requester.department.name}` : ''}
              </Meta>
            </IdentityText>
          </Identity>
          <CloseBtn type='button' aria-label='Close' onClick={onClose}>
            <CloseIcon />
          </CloseBtn>
        </Header>

        <Body>
          <PillRow>
            <PillGroup>
              <StatusPill status={typePill(req.type)}>{typeLabel(req.type)}</StatusPill>
              <StatusPill status={statusPill(req.status)} dot>
                {statusLabel(req.status)}
              </StatusPill>
            </PillGroup>
            <Code>{req.code || ''}</Code>
          </PillRow>

          <DateCards>
            <DateCard>
              <MiniLabel>From</MiniLabel>
              <MiniValue>{formatDateForDisplay(req.from)}</MiniValue>
            </DateCard>
            <DateCard>
              <MiniLabel>To</MiniLabel>
              <MiniValue>{formatDateForDisplay(req.to)}</MiniValue>
            </DateCard>
          </DateCards>

          <MetaGrid>
            <MetaItem>
              <MiniLabel>Coverage</MiniLabel>
              <MiniValue>{req.coverageName || '/'}</MiniValue>
            </MetaItem>
            <MetaItem>
              <MiniLabel>Submitted</MiniLabel>
              <MiniValue>{submittedAgo(req.submittedAt)}</MiniValue>
            </MetaItem>
            <MetaItem>
              <MiniLabel>Duration</MiniLabel>
              <MiniValue>{durationLabel(req)}</MiniValue>
            </MetaItem>
          </MetaGrid>

          {req.type === REQ_SHIFT && req.requestedShift && (
            <MetaItem>
              <MiniLabel>Requested shift</MiniLabel>
              <MiniValue>{req.requestedShift.name || `#${req.requestedShift.id}`}</MiniValue>
            </MetaItem>
          )}

          {req.type === REQ_OVERTIME && (
            <MetaItem>
              <MiniLabel>Extra hours</MiniLabel>
              <MiniValue>+{req.overtimeHours || 0}h</MiniValue>
            </MetaItem>
          )}

          <MetaItem>
            <MiniLabel>Reason</MiniLabel>
            <Reason>{req.reason || '—'}</Reason>
          </MetaItem>

          {req.decision && (
            <DecisionBox>
              <MiniLabel>
                {req.status === ST_APPROVED ? 'Approved' : 'Rejected'} by {userName(req.decision.by)} ·{' '}
                {formatDateForDisplay(req.decision.at)}
              </MiniLabel>
              {req.decision.note && <Reason>{req.decision.note}</Reason>}
            </DecisionBox>
          )}

          {(canDecide || canCancel) && !canDecide && (
            <NoteLabel>You can cancel this request while it is pending.</NoteLabel>
          )}
          {canDecide && (
            <div>
              <NoteLabel>Manager note (required to reject)</NoteLabel>
              <NoteInput
                placeholder='Add a note for the employee…'
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}
        </Body>

        {canDecide && (
          <Actions>
            <Button variant='danger' disabled={!note.trim()} onClick={() => onReject(note.trim())}>
              Reject
            </Button>
            <Button variant='primary' onClick={() => onApprove(note.trim())}>
              Approve
            </Button>
          </Actions>
        )}
        {!canDecide && canCancel && scope === 'mine' && (
          <Actions>
            <Button variant='secondary' onClick={onCancel}>
              Cancel request
            </Button>
          </Actions>
        )}
      </Panel>
    </>
  )
}
